import { useState, useEffect, useMemo } from "react";
import { crewOpsApi } from "../../api";
import { toast } from "sonner";
import {
  Plane, Users, Calendar, CheckCircle, AlertTriangle, RefreshCw,
  ChevronDown, X, Save, Plus, Trash2, Clock, Shield,
} from "lucide-react";

// ─── IST-aware today ──────────────────────────────────────────────────────────
const IST_NOW      = new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000 - new Date().getTimezoneOffset() * 60000);
const TODAY_ISO    = IST_NOW.toISOString().split("T")[0];
const MONTH_PREFIX = TODAY_ISO.slice(0, 7);

const BASE_COLORS = {
  DEL: "#f59e0b", BOM: "#38bdf8", BLR: "#34d399",
  HYD: "#a78bfa", MAA: "#fb923c", CCU: "#f87171",
};

const ROLES = ["Captain", "First Officer", "Cabin Crew 1", "Cabin Crew 2", "Cabin Crew 3"];

function fatigueColor(score) {
  if (score >= 75) return "text-red-400";
  if (score >= 55) return "text-amber-400";
  return "text-green-400";
}

function FatigueBar({ score }) {
  const color = score >= 75 ? "bg-red-500" : score >= 55 ? "bg-amber-500" : "bg-green-500";
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-[9px] font-mono font-bold w-6 text-right ${fatigueColor(score)}`}>{score}</span>
    </div>
  );
}

// ─── Crew Selector Dropdown ───────────────────────────────────────────────────
function CrewSelect({ role, value, onChange, crewPool, disabledIds = [] }) {
  const relevant = crewPool.filter(c => {
    if (role === "Captain") return c.rank === "Captain" || c.rank === "Training Captain";
    if (role === "First Officer") return c.rank === "First Officer";
    return ["Purser", "Senior Cabin Crew", "Cabin Crew"].includes(c.rank);
  });

  const selected = relevant.find(c => c.employee_id === value);
  const hasWarning = selected && selected.fatigue_score >= 75;
  const isHighFatigue = selected && selected.fatigue_score >= 55;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{role}</label>
        {selected && (
          <div className="flex items-center gap-1">
            {hasWarning ? (
              <span className="flex items-center gap-0.5 text-[8px] text-red-400"><AlertTriangle size={8} />High fatigue</span>
            ) : isHighFatigue ? (
              <span className="flex items-center gap-0.5 text-[8px] text-amber-400"><AlertTriangle size={8} />Monitor</span>
            ) : (
              <span className="flex items-center gap-0.5 text-[8px] text-green-400"><CheckCircle size={8} />OK</span>
            )}
          </div>
        )}
      </div>
      <select
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        className={`w-full bg-card border rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all ${
          hasWarning ? "border-red-500/60" : isHighFatigue ? "border-amber-500/50" : "border-border/60"
        }`}
        data-testid={`crew-select-${role.toLowerCase().replace(/ /g, "-")}`}
      >
        <option value="">Unassigned</option>
        {relevant.map(c => (
          <option key={c.employee_id} value={c.employee_id} disabled={disabledIds.includes(c.employee_id) && c.employee_id !== value}>
            {c.name} · FS:{c.fatigue_score} · {c.base}
          </option>
        ))}
      </select>
      {selected && (
        <FatigueBar score={selected.fatigue_score} />
      )}
    </div>
  );
}

// ─── Deployment Card ──────────────────────────────────────────────────────────
function DeploymentCard({ dep, onDelete, deleting }) {
  const [expanded, setExpanded] = useState(false);
  const bc = BASE_COLORS[dep.tail_number?.slice(0, 3)] || "#6366f1";
  const statusBadge = dep.status === "confirmed"
    ? "bg-green-500/15 border-green-500/40 text-green-400"
    : "bg-amber-500/15 border-amber-500/40 text-amber-400";

  return (
    <div className="border border-border/50 rounded-sm bg-card/30 hover:border-border/70 transition-all" data-testid={`deployment-card-${dep.id}`}>
      <div role="button" tabIndex={0} className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer select-none"
        onClick={() => setExpanded(o => !o)}
        onKeyDown={e => e.key === "Enter" && setExpanded(o => !o)}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-1.5 h-8 rounded-full" style={{ backgroundColor: bc }} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[11px] font-bold text-foreground">{dep.tail_number}</span>
              <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-sm border ${statusBadge}`}>
                {dep.status?.toUpperCase()}
              </span>
            </div>
            <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
              {dep.date} · {(dep.sectors || []).join(" → ")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <div className="text-[10px] text-muted-foreground hidden sm:block">
            {(dep.assignments || []).length}/{ROLES.length} crew
          </div>
          <button
            onClick={e => { e.stopPropagation(); onDelete(dep.id); }}
            disabled={deleting === dep.id}
            className="p-1.5 text-muted-foreground/50 hover:text-red-400 border border-border/30 hover:border-red-400/30 rounded-sm transition-all"
            title="Delete deployment"
          >
            {deleting === dep.id ? <RefreshCw size={10} className="animate-spin" /> : <Trash2 size={10} />}
          </button>
          <ChevronDown size={12} className={`text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </div>

      {expanded && dep.assignments?.length > 0 && (
        <div className="border-t border-border/30 px-4 py-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {dep.assignments.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-[10px]">
                <span className="text-muted-foreground/60 w-20 flex-shrink-0">{a.role}:</span>
                <span className="font-mono text-foreground truncate">{a.name}</span>
                <span className={`ml-auto flex-shrink-0 font-mono font-bold text-[9px] ${fatigueColor(a.fatigue_score)}`}>
                  {a.fatigue_score}
                </span>
              </div>
            ))}
          </div>
          {dep.notes && (
            <div className="mt-2 text-[9px] text-muted-foreground/60 border-t border-border/20 pt-2">{dep.notes}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CrewDeployment() {
  const [aircraft,    setAircraft]    = useState([]);
  const [crewPool,    setCrewPool]    = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [pairings,    setPairings]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(null);

  // Form state
  const [selectedTail, setSelectedTail]   = useState("");
  const [selectedDate, setSelectedDate]   = useState(TODAY_ISO);
  const [assignments,  setAssignments]    = useState({});
  const [notes,        setNotes]          = useState("");
  const [editId,       setEditId]         = useState(null);

  // Filter
  const [filterBase,   setFilterBase]     = useState("");
  const [filterStatus, setFilterStatus]   = useState("");

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    try {
      const [acRes, crewRes, depRes, pRes] = await Promise.all([
        crewOpsApi.getFleet(),
        crewOpsApi.getCrew({ limit: 500 }),
        crewOpsApi.listDeployments(),
        crewOpsApi.getPairings({ source: "akasa_schedule" }),
      ]);
      setAircraft(acRes.data?.filter(a => a.status === "active") || []);
      setCrewPool(crewRes.data || []);
      setDeployments(depRes.data || []);
      setPairings(pRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  // Selected aircraft info
  const selectedAc = aircraft.find(a => a.tail_number === selectedTail);
  const acType = selectedAc?.aircraft_type?.replace("Boeing ", "").replace("Airbus ", "") || "B737 MAX 8";

  // Existing deployment for this tail+date (computed early so daySectors can use it)
  const existingDep = deployments.find(d => d.tail_number === selectedTail && d.date === selectedDate);

  // Sectors for selected tail + date (from existing deployment or empty for new)
  const daySectors = useMemo(() => {
    if (existingDep?.sectors?.length > 0) return existingDep.sectors;
    // Try pairings fallback (if pairings have tail_number in future)
    if (!selectedTail || !selectedDate) return [];
    const day = parseInt(selectedDate.split("-")[2]);
    const matched = pairings.filter(p =>
      p.tail_number === selectedTail &&
      p.pairing_type === "flight" &&
      parseInt((p.start_date || "").split("-")[2]) <= day &&
      day <= parseInt((p.end_date || "").split("-")[2])
    );
    return matched.flatMap(p => p.sectors || []);
  }, [selectedTail, selectedDate, pairings, existingDep]);

  useEffect(() => {
    if (existingDep) {
      const map = {};
      existingDep.assignments?.forEach(a => { map[a.role] = a.employee_id; });
      setAssignments(map);
      setNotes(existingDep.notes || "");
      setEditId(existingDep.id);
    } else {
      setAssignments({});
      setNotes("");
      setEditId(null);
    }
  }, [existingDep?.id, selectedTail, selectedDate]);

  // Crew pool B737 qualified
  const b737Crew = useMemo(() =>
    crewPool.filter(c => c.qualified_fleets?.some(f => f.includes("B737") || f.includes("737"))),
    [crewPool]
  );

  const assignedIds = Object.values(assignments).filter(Boolean);

  function setRole(role, empId) {
    setAssignments(prev => ({ ...prev, [role]: empId }));
  }

  // Compliance checks
  const complianceIssues = useMemo(() => {
    const issues = [];
    ROLES.forEach(role => {
      const empId = assignments[role];
      if (!empId) return;
      const member = crewPool.find(c => c.employee_id === empId);
      if (!member) return;
      if (member.fatigue_score >= 75) {
        issues.push({ role, name: member.name, type: "error", msg: `Fatigue score ${member.fatigue_score} ≥ 75 — high risk` });
      } else if (member.fatigue_score >= 55) {
        issues.push({ role, name: member.name, type: "warning", msg: `Fatigue score ${member.fatigue_score} — monitor` });
      }
      if (member.status !== "active") {
        issues.push({ role, name: member.name, type: "error", msg: `Status: ${member.status}` });
      }
    });
    return issues;
  }, [assignments, crewPool]);

  const hasErrors   = complianceIssues.some(i => i.type === "error");
  const filledRoles = ROLES.filter(r => assignments[r]).length;
  const canSave     = selectedTail && selectedDate && filledRoles >= 2;

  async function handleSave(status = "draft") {
    if (!canSave) return;
    setSaving(true);
    try {
      const data = {
        tail_number: selectedTail,
        date: selectedDate,
        sectors: daySectors,
        assignments: ROLES
          .filter(r => assignments[r])
          .map(r => {
            const member = crewPool.find(c => c.employee_id === assignments[r]);
            return {
              role: r,
              employee_id: assignments[r],
              name: member?.name || assignments[r],
              fatigue_score: member?.fatigue_score || 0,
              base: member?.base || "",
              qualified_fleets: member?.qualified_fleets || [],
            };
          }),
        status,
        notes,
      };

      if (editId) {
        await crewOpsApi.updateDeployment(editId, data);
        toast.success(`Deployment updated (${status})`);
      } else {
        await crewOpsApi.createDeployment(data);
        toast.success(`Deployment saved (${status})`);
      }
      await loadAll();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    setDeleting(id);
    try {
      await crewOpsApi.deleteDeployment(id);
      setDeployments(prev => prev.filter(d => d.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
    finally { setDeleting(null); }
  }

  const tails = [...new Set(aircraft.sort((a, b) => a.tail_number.localeCompare(b.tail_number)).map(a => a.tail_number))];

  const filteredDeps = deployments.filter(d => {
    const ac = aircraft.find(a => a.tail_number === d.tail_number);
    if (filterBase && ac?.base !== filterBase) return false;
    if (filterStatus && d.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm animate-pulse">
        Loading Crew Deployment...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background text-foreground overflow-hidden" data-testid="crew-deployment">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-5 py-3 border-b border-border/40 flex-shrink-0">
        <div>
          <h2 className="text-sm font-black uppercase tracking-widest text-foreground">Crew Deployment</h2>
          <div className="text-[10px] text-muted-foreground">
            B737 MAX 8 · Assign crew to daily aircraft schedules
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={loadAll} className="p-1.5 border border-border/30 rounded-sm text-muted-foreground hover:text-foreground transition-colors">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-5 py-2 border-b border-border/30 bg-white/[0.02] flex-shrink-0 flex-wrap">
        {[
          ["Total",     deployments.length, "text-foreground"],
          ["Confirmed", deployments.filter(d => d.status === "confirmed").length, "text-green-400"],
          ["Draft",     deployments.filter(d => d.status === "draft").length, "text-amber-400"],
          ["Aircraft",  aircraft.length, "text-teal-400"],
        ].map(([l, v, cls]) => (
          <div key={l} className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{l}</span>
            <span className={`text-sm font-mono font-black ${cls}`}>{v}</span>
          </div>
        ))}
      </div>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* LEFT: Assignment Form */}
        <div className="w-80 flex-shrink-0 border-r border-border/40 flex flex-col overflow-hidden bg-card/10">
          <div className="px-4 py-3 border-b border-border/30 flex-shrink-0">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
              {editId ? "Edit Deployment" : "New Deployment"}
            </div>

            {/* Aircraft + Date selectors */}
            <div className="space-y-2">
              <div>
                <label className="text-[9px] text-muted-foreground mb-0.5 block font-bold uppercase tracking-widest">Aircraft</label>
                <select value={selectedTail} onChange={e => setSelectedTail(e.target.value)}
                  className="w-full bg-card border border-border/60 rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none"
                  data-testid="deployment-tail-select">
                  <option value="">Select aircraft...</option>
                  {tails.map(t => {
                    const ac = aircraft.find(a => a.tail_number === t);
                    const type = ac?.aircraft_type?.replace("Boeing ", "").replace("Airbus ", "") || "";
                    return <option key={t} value={t}>{`${t} · ${type}${ac?.base ? ` · ${ac.base}` : ""}`}</option>;
                  })}
                </select>
              </div>

              <div>
                <label className="text-[9px] text-muted-foreground mb-0.5 block font-bold uppercase tracking-widest">Date</label>
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                  min={`${MONTH_PREFIX}-01`} max={`${MONTH_PREFIX}-31`}
                  className="w-full bg-card border border-border/60 rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none"
                  data-testid="deployment-date-input" />
              </div>
            </div>

            {/* Sectors preview */}
            {selectedTail && (
              <div className="mt-2 p-2 bg-white/5 border border-border/30 rounded-sm">
                <div className="text-[9px] text-muted-foreground mb-1">Sectors on {selectedDate}</div>
                {daySectors.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {daySectors.map((s, i) => (
                      <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-500/15 border border-amber-500/30 rounded-sm text-amber-300">{s}</span>
                    ))}
                  </div>
                ) : (
                  <div className="text-[9px] text-muted-foreground/50 italic">No scheduled sectors found — custom deployment</div>
                )}
              </div>
            )}

            {existingDep && (
              <div className="mt-1.5 px-2 py-1 bg-teal-500/10 border border-teal-500/25 rounded-sm text-[9px] text-teal-400 flex items-center gap-1">
                <CheckCircle size={9} /> Editing existing deployment
              </div>
            )}
          </div>

          {/* Crew assignment fields */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {selectedTail ? (
              <>
                {ROLES.map(role => (
                  <CrewSelect
                    key={role}
                    role={role}
                    value={assignments[role] || ""}
                    onChange={empId => setRole(role, empId)}
                    crewPool={b737Crew.length > 0 ? b737Crew : crewPool}
                    disabledIds={assignedIds}
                  />
                ))}

                <div>
                  <label className="text-[9px] text-muted-foreground mb-0.5 block font-bold uppercase tracking-widest">Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    rows={2}
                    placeholder="Optional notes..."
                    className="w-full bg-card border border-border/60 rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none resize-none" />
                </div>

                {/* Compliance issues */}
                {complianceIssues.length > 0 && (
                  <div className="space-y-1">
                    {complianceIssues.map((issue, i) => (
                      <div key={i} className={`flex items-start gap-1.5 px-2 py-1.5 rounded-sm text-[9px] ${
                        issue.type === "error"
                          ? "bg-red-500/10 border border-red-500/25 text-red-400"
                          : "bg-amber-500/10 border border-amber-500/25 text-amber-400"
                      }`}>
                        <AlertTriangle size={9} className="flex-shrink-0 mt-0.5" />
                        <span><span className="font-bold">{issue.role}:</span> {issue.msg}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-[10px] text-muted-foreground/40 italic text-center py-6">
                Select an aircraft above to begin crew assignment.
              </div>
            )}
          </div>

          {/* Save bar */}
          <div className="px-4 py-3 border-t border-border/30 flex-shrink-0 space-y-2">
            <div className="flex gap-2">
              <button
                onClick={() => handleSave("draft")}
                disabled={!canSave || saving}
                className="flex-1 py-1.5 text-[10px] font-bold rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 transition-all"
                data-testid="save-draft-btn"
              >
                {saving ? <RefreshCw size={10} className="inline animate-spin mr-1" /> : null}
                Save Draft
              </button>
              <button
                onClick={() => handleSave("confirmed")}
                disabled={!canSave || saving || hasErrors}
                className="flex-1 py-1.5 text-[10px] font-bold rounded-sm bg-green-500/15 border border-green-500/40 text-green-300 hover:bg-green-500/25 disabled:opacity-40 transition-all"
                data-testid="confirm-deployment-btn"
              >
                <CheckCircle size={10} className="inline mr-1" />
                Confirm
              </button>
            </div>
            {hasErrors && (
              <div className="text-[8px] text-red-400 text-center">Resolve errors before confirming</div>
            )}
            <div className="text-[8px] text-muted-foreground/40 text-center">
              {filledRoles}/{ROLES.length} roles assigned
            </div>
          </div>
        </div>

        {/* RIGHT: Deployments list */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Filter bar */}
          <div className="flex items-center gap-3 px-5 py-2.5 border-b border-border/30 flex-shrink-0 bg-white/[0.01]">
            <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Filter</span>
            <select value={filterBase} onChange={e => setFilterBase(e.target.value)}
              className="bg-card border border-border/50 rounded-sm px-2 py-1 text-xs text-foreground focus:outline-none"
              data-testid="dep-base-filter">
              <option value="">All Bases</option>
              {["DEL","BOM","BLR","HYD","MAA","CCU"].map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="bg-card border border-border/50 rounded-sm px-2 py-1 text-xs text-foreground focus:outline-none"
              data-testid="dep-status-filter">
              <option value="">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="draft">Draft</option>
            </select>
            <span className="ml-auto text-[10px] text-muted-foreground">{filteredDeps.length} deployments</span>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-5 space-y-2">
            {filteredDeps.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center border border-border/20 rounded-sm bg-white/[0.01]">
                <Users size={32} className="text-muted-foreground/20 mb-3" />
                <div className="text-sm text-muted-foreground">No deployments found</div>
                <div className="text-[11px] text-muted-foreground/50 mt-1">
                  Select an aircraft + date on the left to create one.
                </div>
              </div>
            ) : (
              filteredDeps.map(dep => (
                <DeploymentCard key={dep.id} dep={dep} onDelete={handleDelete} deleting={deleting} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
